import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { Alert, Button, Card, CardHeader, CardBody, CardFooter, Form, Input, InputGroupText, InputGroup, FormGroup, Label } from 'reactstrap';
import toast from 'react-hot-toast';

import { frontend } from '../../api';
import { validateDate, validateInt } from '../../validator';
import { FaRegCalendarAlt, FaSpinner, FaUser } from 'react-icons/fa';
import { FailedToFetchException } from 'api/exceptions/FailedToFetchException';
import { InvalidParametersException } from 'api/exceptions/InvalidParametersException';
import { ForbiddenException } from 'api/exceptions/ForbiddenException';
import { ResourceNotFoundException } from 'api/exceptions/ResourceNotFoundException';
import { InternalServerErrorException } from 'api/exceptions/InternalServerErrorException';

interface Props {
	onVerifyStepClick: (data: { bsaId?: string; dob?: string; loginToken?: string; verificationCodeEmail?: string }) => void;
}

export const CheckClearances = (props: Props) => {
	const { onVerifyStepClick } = props;
	const [checkButtonEnabled, setCheckButtonEnabled] = useState(false);
	const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false);
	const [validationMessage, setValidationMessage] = useState<string | null>(null);
	const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
	const [messageState, setMessageState] = useState('info');
	const [message, setMessage] = useState<string | null>(null);
	const [data, setData] = useState<{ bsaId?: string; dob?: string; loginToken?: string; verificationCodeEmail?: string }>({});
	const [loginMessage, setLoginMessage] = useState<string | null>(null);

	useEffect(() => {
		const getContents = async () => {
			try {
				const result = await frontend.getMessage();

				if (result != null) {
					setLoginMessage(result);
				}
			} catch (error) {
				if (error instanceof FailedToFetchException) {
					toast.error(error.message);
				} else {
					Sentry.captureException(error);
				}
			}
		};

		getContents();
	}, []);

	useEffect(() => {
		let canSave = true;
		let validationMsg: string | null = null;

		if (data.bsaId == null || data.bsaId === '') {
			canSave = false;
		} else if (!validateInt(data.bsaId!)) {
			canSave = false;
			validationMsg = 'Enter your BSA ID number';
		} else if (data.dob == null || data.dob === '') {
			canSave = false;
		} else if (!validateDate(data.dob!)) {
			canSave = false;
		} else if (canSave) {
			const currentDate = new Date();
			currentDate.setUTCHours(0, 0, 0, 0);
			currentDate.setUTCDate(currentDate.getUTCDate() - 1);

			const oldDate = new Date(1900, 0, 1, 0, 0, 0, 0);

			const dobObj = new Date(data.dob!);
			if (dobObj.getTime() >= currentDate.getTime() || dobObj.getTime() < oldDate.getTime()) {
				canSave = false;
				validationMsg = 'Invalid date entered';
			}
		}

		setCheckButtonEnabled(canSave);
		setValidationMessage(validationMsg);
	}, [data]);

	const handleInputFieldChange = (fieldName: 'bsaId' | 'dob', newValue: any) => {
		const newData = { ...data };
		newData[fieldName] = newValue;
		setData(newData);
		setShowValidationMessage(true);
	};

	const handleCheckClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setMessageState('info');
		setMessage(null);
		setIsSubmitInProgress(true);

		try {
			const result = await frontend.check(data.bsaId!, data.dob!);
			const newData = { ...data };
			newData.loginToken = result.token;
			newData.verificationCodeEmail = result.verificationCodeEmail;
			onVerifyStepClick(newData);
		} catch (error) {
			if (error instanceof InvalidParametersException) {
				setMessageState('danger');
				setMessage(error.message);
				setIsSubmitInProgress(false);
			} else if (error instanceof ForbiddenException) {
				setMessageState('danger');
				setMessage(error.message);
				setIsSubmitInProgress(false);
			} else if (error instanceof ResourceNotFoundException) {
				setMessageState('danger');
				setMessage(error.message);
				setIsSubmitInProgress(false);
			} else if (error instanceof InternalServerErrorException) {
				setMessageState('danger');
				setMessage(error.message);
				setIsSubmitInProgress(false);
			} else if (error instanceof FailedToFetchException) {
				setMessageState('danger');
				setMessage(error.message);
				setIsSubmitInProgress(false);
			} else {
				setMessageState('danger');
				setMessage('An error occurred. Please try again later.');
				setIsSubmitInProgress(false);
				Sentry.captureException(error);
			}
		}
	};

	return (
		<Form action="" className="form" method="" onSubmit={(e) => handleCheckClick(e)}>
			<Card className="card-login">
				<CardHeader>
					<CardHeader>
						<h3 className="header text-center">PA Clearances Portal</h3>
					</CardHeader>
				</CardHeader>
				<CardBody>
					{message != null && (
						<Alert color={messageState}>
							<span>{message}</span>
						</Alert>
					)}
					{showValidationMessage && validationMessage != null && (
						<Alert color="primary">
							<span>{validationMessage}</span>
						</Alert>
					)}
					{loginMessage != null && loginMessage.length > 0 && <p dangerouslySetInnerHTML={{ __html: loginMessage }} />}
					<FormGroup>
						<Label for="bsaId">BSA Member ID</Label>
						<InputGroup>
							<InputGroupText style={{ background: 'none' }}>
								<FaUser color="#66615b" />
							</InputGroupText>
							<Input
								id="bsaId"
								name="bsaId"
								type="number"
								placeholder="Enter BSA ID"
								onChange={(e) => handleInputFieldChange('bsaId', e.target.value)}
							/>
						</InputGroup>
					</FormGroup>
					<FormGroup>
						<Label for="dob">Date of Birth</Label>
						<InputGroup>
							<InputGroupText style={{ background: 'none' }}>
								<FaRegCalendarAlt color="#66615b" />
							</InputGroupText>
							<Input id="dob" type="date" placeholder="Date of Birth" onChange={(e) => handleInputFieldChange('dob', e.target.value)} />
						</InputGroup>
					</FormGroup>
				</CardBody>
				<CardFooter>
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<div style={{ width: '100%' }}>
							<Button
								block
								className="btn-round mb-3"
								color="primary"
								type="button"
								disabled={isSubmitInProgress || (!isSubmitInProgress && !checkButtonEnabled)}
								onClick={(e) => handleCheckClick(e)}
							>
								{isSubmitInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Check'}
							</Button>
						</div>
						{process.env.REACT_APP_GUEST_MEMBERS === 'true' && (
							<div>
								<a href="/app/new-member" style={{ color: '#000' }}>
									Don't have a BSA Member ID? Click here
								</a>
							</div>
						)}
					</div>
				</CardFooter>
			</Card>
		</Form>
	);
};
